import React, { useState } from 'react';
import styles from './InstructionCard.module.css';

interface InstructionCardProps {
  title: string;
  content: string[];
  emoji?: string;
  storageKey?: string;
  firstTimeUserContent?: string[]; // Alternative content for first-time users
  isFirstTimeUser?: boolean;
  persistUntilAction?: boolean; // Don't allow dismissal until user takes action
  actionRequired?: string; // Description of required action
}

const InstructionCard: React.FC<InstructionCardProps> = ({ 
  title, 
  content, 
  emoji = '💡', 
  storageKey,
  firstTimeUserContent,
  isFirstTimeUser = false,
  persistUntilAction = false,
  actionRequired
}) => {
  const [isVisible, setIsVisible] = useState(() => {
    if (storageKey) {
      return localStorage.getItem(storageKey) !== 'dismissed';
    }
    return true;
  });

  const handleDismiss = () => {
    // Don't allow dismissal if action is required and user is first-time
    if (persistUntilAction && isFirstTimeUser) {
      return;
    }
    
    if (storageKey) {
      localStorage.setItem(storageKey, 'dismissed');
    }
    setIsVisible(false);
  };

  // Choose content based on whether it's a first-time user
  const displayContent = isFirstTimeUser && firstTimeUserContent ? firstTimeUserContent : content;

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.instructionCard}>
      <div className={styles.header}>
        <div className={styles.title}>
          {emoji} {title}
        </div>
        <button 
          className={styles.dismissButton}
          onClick={handleDismiss}
          aria-label="Dismiss instruction"
        >
          ×
        </button>
      </div>
      <div className={styles.content}>
        {displayContent.map((paragraph, index) => (
          <div key={index} className={styles.paragraph}>
            {paragraph}
          </div>
        ))}
        {actionRequired && isFirstTimeUser && (
          <div className={styles.actionRequired}>
            <strong>Action Required:</strong> {actionRequired}
          </div>
        )}
      </div>
      <div className={styles.buttonContainer}>
        <button 
          className={`${styles.gotItButton} ${persistUntilAction && isFirstTimeUser ? styles.disabled : ''}`}
          onClick={handleDismiss}
          disabled={persistUntilAction && isFirstTimeUser}
        >
          {persistUntilAction && isFirstTimeUser ? 'Complete Action First' : 'Got It'}
        </button>
      </div>
    </div>
  );
};

export default InstructionCard;